import React, { useState, useRef } from 'react';
import './BulkCourseImport.css';

const BulkCourseImport = ({ onBack, onImportSuccess, defaultPaymentBody }) => {
  const [stage, setStage] = useState('upload'); // 'upload' | 'review' | 'importing'
  const [file, setFile] = useState(null);
  const [ignoreHolidays, setIgnoreHolidays] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Staging data
  const [events, setEvents] = useState([]);
  const [existingCourses, setExistingCourses] = useState([]);
  const [summary, setSummary] = useState({
    totalEvents: 0,
    validCourses: 0,
    unmatchedCourseNames: 0,
    skippedHolidays: 0,
    duplicates: 0
  });

  // Filters & Search
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'matched' | 'new' | 'duplicate' | 'holiday'
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkCapacityInput, setBulkCapacityInput] = useState(10);

  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processSelectedFile(e.target.files[0]);
    }
  };

  const processSelectedFile = (selectedFile) => {
    setErrorMsg('');
    if (!selectedFile.name.toLowerCase().endsWith('.ics') && selectedFile.type !== 'text/calendar') {
      setErrorMsg('Please upload a valid iCalendar (.ics) file.');
      return;
    }
    if (selectedFile.size === 0) {
      setErrorMsg('The selected file is empty.');
      return;
    }
    setFile(selectedFile);
    parseFile(selectedFile, ignoreHolidays);
  };

  const parseFile = async (fileToParse, skipHolidays) => {
    setLoading(true);
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('file', fileToParse);
      formData.append('ignoreHolidays', String(skipHolidays));

      const res = await fetch('/api/admin/courses/parse-ics', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setEvents(data.events || []);
        setExistingCourses(data.existingCourses || []);
        setSummary(data.summary || {
          totalEvents: (data.events || []).length,
          validCourses: 0,
          unmatchedCourseNames: 0,
          skippedHolidays: 0,
          duplicates: 0
        });
        setStage('review');
      } else {
        setErrorMsg(data.message || 'Failed to parse the calendar file.');
      }
    } catch (err) {
      console.error('[Full Page ICS Import Error]:', err);
      setErrorMsg('Failed to communicate with the server. Ensure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  // ── Staging Actions ──
  const handleToggleInclude = (stagingId) => {
    setEvents(prev =>
      prev.map(item =>
        item.stagingId === stagingId ? { ...item, include: !item.include } : item
      )
    );
  };

  const handleMasterToggle = (checked) => {
    setEvents(prev => prev.map(item => ({ ...item, include: checked })));
  };

  const handleTitleChange = (stagingId, newTitle) => {
    setEvents(prev =>
      prev.map(item =>
        item.stagingId === stagingId ? { ...item, parsedTitle: newTitle } : item
      )
    );
  };

  const handleMappedCourseChange = (stagingId, value) => {
    setEvents(prev =>
      prev.map(item => {
        if (item.stagingId !== stagingId) return item;

        if (value === 'new') {
          return {
            ...item,
            matchedCourseId: 'new',
            matchedCourseName: null,
            status: 'new'
          };
        }

        const selectedCourse = existingCourses.find(c => String(c.courseId) === String(value));
        if (selectedCourse) {
          return {
            ...item,
            matchedCourseId: selectedCourse.courseId,
            matchedCourseName: `${selectedCourse.certificationBody ? selectedCourse.certificationBody + ' ' : ''}${selectedCourse.courseName}`,
            certificationBody: selectedCourse.certificationBody || item.certificationBody,
            status: 'matched'
          };
        }

        return item;
      })
    );
  };

  const handleCapacityChange = (stagingId, value) => {
    const val = value === '' ? '' : Math.max(1, parseInt(value, 10) || 1);
    setEvents(prev =>
      prev.map(item =>
        item.stagingId === stagingId ? { ...item, maxCapacity: val } : item
      )
    );
  };

  const handleApplyBulkCapacity = (customValue) => {
    let val = parseInt(customValue !== undefined && typeof customValue !== 'object' ? customValue : bulkCapacityInput, 10);
    if (isNaN(val) || val <= 0) {
      val = 10;
    }
    setBulkCapacityInput(val);
    setEvents(prev =>
      prev.map(item =>
        item.status === 'holiday' || item.isNonCourse
          ? item
          : { ...item, maxCapacity: val }
      )
    );
  };

  const handleDismissRow = (stagingId) => {
    setEvents(prev => {
      const updated = prev.filter(item => item.stagingId !== stagingId);
      // Recalculate summary metrics dynamically
      const total = updated.length;
      const valid = updated.filter(e => e.status === 'matched' || e.status === 'new').length;
      const unmatched = updated.filter(e => e.status === 'new').length;
      const holidays = updated.filter(e => e.status === 'holiday').length;
      const dups = updated.filter(e => e.status === 'duplicate').length;

      setSummary({
        totalEvents: total,
        validCourses: valid,
        unmatchedCourseNames: unmatched,
        skippedHolidays: holidays,
        duplicates: dups
      });

      return updated;
    });
  };

  // ── Commit Bulk Import ──
  const handleConfirmImport = async () => {
    const includedItems = events.filter(e => e.include);
    if (includedItems.length === 0) {
      setErrorMsg('No courses are selected for import.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    const payloadItems = includedItems.map(item => ({
      ...item,
      maxCapacity: (parseInt(item.maxCapacity, 10) && parseInt(item.maxCapacity, 10) > 0)
        ? parseInt(item.maxCapacity, 10)
        : 10
    }));

    try {
      const res = await fetch('/api/admin/courses/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: payloadItems,
          defaultPaymentBody: defaultPaymentBody || null
        })
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        if (onImportSuccess) {
          onImportSuccess(data);
        }
      } else {
        setErrorMsg(data.message || 'Failed to complete bulk import.');
      }
    } catch (err) {
      console.error('[Bulk Commit Error]:', err);
      setErrorMsg('Server communication error during import commit.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Filtered Events
  const filteredEvents = events.filter(evt => {
    if (statusFilter !== 'all' && evt.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const titleMatch = (evt.parsedTitle || '').toLowerCase().includes(q);
      const courseMatch = (evt.matchedCourseName || '').toLowerCase().includes(q);
      if (!titleMatch && !courseMatch) return false;
    }
    return true;
  });

  const includedCount = events.filter(e => e.include).length;
  const allIncluded = events.length > 0 && events.every(e => e.include);
  const someIncluded = events.some(e => e.include) && !allIncluded;

  return (
    <div className="bulk-import-page-container" data-testid="bulk-import-page">
      {/* ── Top Bar & Breadcrumb Navigation ── */}
      <header className="bulk-import-top-bar">
        <div className="bulk-import-top-bar-left">
          <div className="bulk-import-breadcrumb">
            <span className="breadcrumb-link" onClick={onBack}>Courses</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Bulk Import (.ics)</span>
          </div>
          <h1 className="bulk-import-header-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span>Bulk Course Import via iCalendar (.ics)</span>
          </h1>
          <p className="bulk-import-header-subtitle">
            Upload, preview, stage, and batch-create course sessions with full catalog resolution.
          </p>
        </div>

        <div className="bulk-import-top-bar-right">
          <button
            type="button"
            className="back-to-courses-btn"
            onClick={onBack}
            data-testid="back-to-courses-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            <span>Back to Courses</span>
          </button>
        </div>
      </header>

      {errorMsg && (
        <div className="bulk-import-page-error" data-testid="import-error-banner">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ── STAGE 1: File Upload Screen ── */}
      {stage === 'upload' ? (
        <div className="bulk-import-card">
          <div
            className={`full-page-dropzone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="ics-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".ics,text/calendar"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              data-testid="ics-file-input"
            />
            <div className="dropzone-icon-large">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <h3 className="dropzone-title-large">
              {file ? `Selected File: ${file.name}` : 'Drag and drop your .ics calendar file here'}
            </h3>
            <p className="dropzone-desc-large">
              Supports CCOE Malaysia Outlook schedules and standard RFC 5545 calendar exports. Files are parsed securely with line unfolding and timezone normalization.
            </p>
            <button
              type="button"
              className="dropzone-btn-large"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse Computer (.ics)
            </button>
          </div>

          {/* Holiday filter and parsing options */}
          <div className="bulk-import-options-card">
            <label className="holiday-toggle-wrapper" htmlFor="ignore-holidays-toggle">
              <input
                type="checkbox"
                id="ignore-holidays-toggle"
                className="holiday-toggle-checkbox"
                checked={ignoreHolidays}
                onChange={(e) => setIgnoreHolidays(e.target.checked)}
                data-testid="ignore-holidays-checkbox"
              />
              <div className="holiday-toggle-text">
                <span className="holiday-toggle-title">Ignore Public Holidays & non-course events</span>
                <span className="holiday-toggle-desc">Automatically identify and exclude public holidays or office closed dates from course creation.</span>
              </div>
            </label>
          </div>

          {/* Format Highlights */}
          <div className="import-feature-highlights">
            <div className="feature-highlight-card">
              <div className="feature-highlight-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </div>
              <div>
                <h4 className="feature-highlight-title">RFC 5545 Line Unfolding</h4>
                <p className="feature-highlight-desc">Handles folded multi-line descriptions and long UIDs seamlessly without corruption.</p>
              </div>
            </div>

            <div className="feature-highlight-card">
              <div className="feature-highlight-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <h4 className="feature-highlight-title">Timezone Normalization</h4>
                <p className="feature-highlight-desc">Converts Singapore Standard Time and UTC timestamps accurately to local +08:00 session times.</p>
              </div>
            </div>

            <div className="feature-highlight-card">
              <div className="feature-highlight-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <polyline points="17 11 19 13 23 9"/>
                </svg>
              </div>
              <div>
                <h4 className="feature-highlight-title">Catalog Auto-Matching</h4>
                <p className="feature-highlight-desc">Detects existing courses in your database via exact, acronym, and fuzzy similarity matching.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ── STAGE 2: Full-Page Staging & Review Screen ── */}
          {/* Summary Banner */}
          <div className="full-page-summary-banner" data-testid="staging-summary-banner">
            <div className="summary-tile total">
              <span className="summary-tile-label">Total Detected</span>
              <span className="summary-tile-value" data-testid="summary-total">{summary.totalEvents}</span>
              <span className="summary-tile-hint">All parsed calendar events</span>
            </div>
            <div className="summary-tile valid">
              <span className="summary-tile-label">Valid Courses</span>
              <span className="summary-tile-value" data-testid="summary-valid">{summary.validCourses}</span>
              <span className="summary-tile-hint">Matched catalog or new definitions</span>
            </div>
            <div className="summary-tile unmatched">
              <span className="summary-tile-label">Unmatched Titles</span>
              <span className="summary-tile-value" data-testid="summary-unmatched">{summary.unmatchedCourseNames}</span>
              <span className="summary-tile-hint">Will create new course catalog entries</span>
            </div>
            <div className="summary-tile holiday">
              <span className="summary-tile-label">Skipped Holidays</span>
              <span className="summary-tile-value" data-testid="summary-holidays">{summary.skippedHolidays}</span>
              <span className="summary-tile-hint">Public holidays & non-course events</span>
            </div>
            <div className="summary-tile duplicate">
              <span className="summary-tile-label">Duplicates</span>
              <span className="summary-tile-value" data-testid="summary-duplicates">{summary.duplicates}</span>
              <span className="summary-tile-hint">Overlapping sessions or duplicate UIDs</span>
            </div>
          </div>

          {/* Staging Toolbar: Filters & Search */}
          <div className="staging-toolbar">
            <div className="filter-chips-group">
              <button
                type="button"
                className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
                data-testid="filter-all"
              >
                All Events ({events.length})
              </button>
              <button
                type="button"
                className={`filter-chip ${statusFilter === 'matched' ? 'active' : ''}`}
                onClick={() => setStatusFilter('matched')}
                data-testid="filter-matched"
              >
                🟢 Matched Existing ({events.filter(e => e.status === 'matched').length})
              </button>
              <button
                type="button"
                className={`filter-chip ${statusFilter === 'new' ? 'active' : ''}`}
                onClick={() => setStatusFilter('new')}
                data-testid="filter-new"
              >
                🟡 New Titles ({events.filter(e => e.status === 'new').length})
              </button>
              <button
                type="button"
                className={`filter-chip ${statusFilter === 'holiday' ? 'active' : ''}`}
                onClick={() => setStatusFilter('holiday')}
                data-testid="filter-holiday"
              >
                ⚪ Holidays ({events.filter(e => e.status === 'holiday').length})
              </button>
              <button
                type="button"
                className={`filter-chip ${statusFilter === 'duplicate' ? 'active' : ''}`}
                onClick={() => setStatusFilter('duplicate')}
                data-testid="filter-duplicate"
              >
                🔴 Duplicates ({events.filter(e => e.status === 'duplicate').length})
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="batch-capacity-toolbar-box" title="Batch update capacity for all courses">
                <span className="batch-capacity-label">Default Capacity:</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  className="batch-capacity-input"
                  value={bulkCapacityInput}
                  onChange={(e) => setBulkCapacityInput(e.target.value)}
                  data-testid="toolbar-bulk-capacity-input"
                />
                <button
                  type="button"
                  className="btn-secondary-page"
                  style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  onClick={handleApplyBulkCapacity}
                  data-testid="toolbar-bulk-capacity-apply-btn"
                >
                  Apply to All
                </button>
              </div>

              <div className="staging-search-box">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Filter staged courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-staged-input"
                />
              </div>
              <button
                type="button"
                className="btn-secondary-page"
                style={{ padding: '0.45rem 0.9rem', fontSize: '0.825rem' }}
                onClick={() => {
                  setStage('upload');
                  setFile(null);
                }}
              >
                Re-upload
              </button>
            </div>
          </div>

          {/* Full-Width Staging Review Data Table */}
          <div className="full-page-table-wrapper">
            <table className="full-page-table" data-testid="staging-review-table">
              <thead>
                <tr>
                  <th style={{ width: '44px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allIncluded}
                      ref={el => { if (el) el.indeterminate = someIncluded; }}
                      onChange={(e) => handleMasterToggle(e.target.checked)}
                      title="Toggle all rows"
                      data-testid="master-include-checkbox"
                    />
                  </th>
                  <th style={{ width: '190px' }}>Status / Match</th>
                  <th>Parsed Title</th>
                  <th style={{ width: '310px' }}>Mapped Course Definition</th>
                  <th style={{ width: '130px' }}>Mode / Series</th>
                  <th style={{ width: '180px' }}>
                    <div className="capacity-th-container">
                      <span>Capacity</span>
                      <div className="th-bulk-capacity-wrapper" title="Set capacity for all courses">
                        <input
                          type="number"
                          min="1"
                          max="999"
                          value={bulkCapacityInput}
                          onChange={(e) => setBulkCapacityInput(e.target.value)}
                          className="th-capacity-input"
                          data-testid="header-bulk-capacity-input"
                        />
                        <button
                          type="button"
                          className="th-apply-all-btn"
                          title="Apply this capacity to all courses"
                          onClick={() => handleApplyBulkCapacity()}
                          data-testid="header-apply-capacity-btn"
                        >
                          Set All
                        </button>
                      </div>
                    </div>
                  </th>
                  <th style={{ width: '160px' }}>Start Date & Time</th>
                  <th style={{ width: '160px' }}>End Date & Time</th>
                  <th style={{ width: '60px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                      No course events match the current filter.
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map(evt => (
                    <tr
                      key={evt.stagingId}
                      className={!evt.include ? 'row-excluded' : ''}
                      data-testid={`staging-row-${evt.stagingId}`}
                    >
                      {/* 1. Include? Checkbox */}
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={evt.include}
                          onChange={() => handleToggleInclude(evt.stagingId)}
                          data-testid={`checkbox-${evt.stagingId}`}
                        />
                      </td>

                      {/* 2. Status / Match Badge */}
                      <td>
                        {evt.status === 'matched' ? (
                          <span className="fp-status-badge status-badge matched" title="Found matching course in catalog">
                            🟢 Matched Existing Course
                          </span>
                        ) : evt.status === 'new' ? (
                          <span className="fp-status-badge status-badge new" title="Will create a new course definition in catalog">
                            🟡 New Course Title
                          </span>
                        ) : evt.status === 'duplicate' ? (
                          <span className="fp-status-badge status-badge duplicate" title="Course session on same date/time or UID already exists">
                            🔴 Duplicate Session
                          </span>
                        ) : (
                          <span className="fp-status-badge status-badge holiday" title="Identified as holiday or non-course event">
                            ⚪ Non-Course / Holiday
                          </span>
                        )}
                      </td>

                      {/* 3. Parsed Title (Editable Input) */}
                      <td>
                        <input
                          type="text"
                          className="table-text-input staging-input"
                          value={evt.parsedTitle}
                          onChange={(e) => handleTitleChange(evt.stagingId, e.target.value)}
                          data-testid={`input-title-${evt.stagingId}`}
                        />
                      </td>

                      {/* 4. Mapped Course Selector */}
                      <td>
                        <select
                          className="table-select-input staging-select"
                          value={evt.matchedCourseId || 'new'}
                          onChange={(e) => handleMappedCourseChange(evt.stagingId, e.target.value)}
                          data-testid={`select-course-${evt.stagingId}`}
                        >
                          <option value="new">(+) Create as New Course definition</option>
                          <optgroup label="Existing Courses in Catalog">
                            {existingCourses.map(course => (
                              <option key={course.courseId} value={course.courseId}>
                                {course.certificationBody ? `[${course.certificationBody}] ` : ''}{course.courseName}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </td>

                      {/* 5. Delivery Mode & Series */}
                      <td>
                        <span className={`fp-pill-tag ${evt.deliveryMode === 'Online' ? 'online' : 'physical'}`}>
                          {evt.deliveryMode || 'Physical'}
                        </span>
                        {evt.isSeriesPart && (
                          <span className="fp-pill-tag series" title={`Series Part: ${evt.seriesPart}`}>
                            {evt.seriesPart}
                          </span>
                        )}
                      </td>

                      {/* 6. Capacity */}
                      <td>
                        {evt.status === 'holiday' || evt.isNonCourse ? (
                          <div style={{ textAlign: 'center' }}>
                            <span className="capacity-na-text" title="Not applicable for public holidays / non-course events">—</span>
                          </div>
                        ) : (
                          <div className="row-capacity-wrapper">
                            <input
                              type="number"
                              min="1"
                              max="999"
                              className="table-capacity-input"
                              value={evt.maxCapacity ?? 10}
                              onChange={(e) => handleCapacityChange(evt.stagingId, e.target.value)}
                              data-testid={`input-capacity-${evt.stagingId}`}
                            />
                            <span className="capacity-unit">seats</span>
                          </div>
                        )}
                      </td>

                      {/* 7. Start Date & Time */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <strong>{formatDateDisplay(evt.startDate)}</strong>
                        <div style={{ fontSize: '0.785rem', color: '#64748b' }}>{evt.startTime}</div>
                      </td>

                      {/* 7. End Date & Time */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <strong>{formatDateDisplay(evt.endDate)}</strong>
                        <div style={{ fontSize: '0.785rem', color: '#64748b' }}>{evt.endTime}</div>
                      </td>

                      {/* 8. Row Dismiss Action */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="fp-delete-btn row-delete-btn"
                          onClick={() => handleDismissRow(evt.stagingId)}
                          title="Dismiss row from batch queue"
                          data-testid={`delete-row-${evt.stagingId}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Sticky Bottom Action Bar ── */}
          <div className="bulk-import-sticky-bar">
            <div className="sticky-bar-left">
              <strong>{includedCount}</strong> of <strong>{events.length}</strong> course session(s) selected for import
            </div>
            <div className="sticky-bar-right">
              <button
                type="button"
                className="btn-secondary-page"
                onClick={onBack}
                disabled={loading}
                data-testid="cancel-import-btn"
              >
                Cancel & Return
              </button>
              <button
                type="button"
                className="btn-primary-page"
                onClick={handleConfirmImport}
                disabled={loading || includedCount === 0}
                data-testid="confirm-import-btn"
              >
                {loading ? (
                  <>
                    <span className="page-spinner"></span>
                    <span>Importing Courses...</span>
                  </>
                ) : (
                  <span>Confirm & Import ({includedCount} Courses)</span>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkCourseImport;
